import '../App.css';

import { FC, useCallback, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

import { ImageUploader } from '@/components/ImageUploader/ImageUploader';
import { SimilarMatchCard } from '@/components/search/SimilarMatchCard';
import { NbButton, NbCard, NbChip } from '@/components/Neobrut';
import { searchPetsBySimilarImage } from '@/api/similarByImage';

const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

const SCOPE_OPTIONS: { label: string; value: 'all' | PetStatus }[] = [
  { label: 'Todos', value: 'all' },
  { label: 'Perdidos', value: 'lost' },
  { label: 'Encontrados', value: 'found' },
];

export const SearchByPhoto: FC = () => {
  const [uploaderKey, setUploaderKey] = useState(0);
  const [queryFile, setQueryFile] = useState<File | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | PetStatus>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [matches, setMatches] = useState<SimilarPetMatch[] | null>(null);

  const onImagesSelected = useCallback((files: File[]) => {
    setError(null);
    setMatches(null);
    const f = files[0];
    if (!f) {
      setQueryFile(null);
      return;
    }
    if (f.size > MAX_UPLOAD_BYTES) {
      setError('La imagen recortada supera 5MB.');
      setQueryFile(null);
      setUploaderKey((k) => k + 1);
      return;
    }
    setQueryFile(f);
  }, []);

  const onSearch = useCallback(async () => {
    if (!queryFile) return;
    setLoading(true);
    setError(null);
    setMatches(null);
    try {
      const data = await searchPetsBySimilarImage(queryFile, {
        limit: 12,
        status: statusFilter === 'all' ? undefined : statusFilter,
      });
      setMatches(data.results);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const detail = err.response?.data?.detail;
        setError(
          typeof detail === 'string' ? detail : 'No se pudo buscar. Probá de nuevo más tarde.',
        );
      } else {
        setError('Ocurrió un error inesperado.');
      }
    } finally {
      setLoading(false);
    }
  }, [queryFile, statusFilter]);

  return (
    <div className="mx-auto flex w-full max-w-xl lg:max-w-3xl flex-col gap-6 px-4 pb-12">
      <header>
        <div
          className="text-[11px] font-bold uppercase tracking-[0.08em] opacity-60"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          / buscar-por-foto
        </div>
        <h1 className="font-display mt-1 text-[40px] leading-[0.9]">
          Subí una
          <br />
          foto. <span className="nb-highlight">listo.</span>
        </h1>
        <p className="mt-3 text-[13px] font-medium">
          Comparamos color, manchas y forma contra todas las publicaciones activas. Tarda ~3
          segundos.
        </p>
      </header>

      <NbCard className="p-4">
        <div
          className="text-[10px] font-bold uppercase tracking-[0.08em] opacity-60"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          01 / foto
        </div>

        <div className="mt-2">
          <ImageUploader
            key={uploaderKey}
            maxImages={1}
            className="p-0"
            onImagesSelected={onImagesSelected}
          />
          <div
            className="mt-2 text-[10px] font-bold uppercase tracking-[0.08em] opacity-70"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            JPG · PNG · HASTA 5MB
          </div>
        </div>

        <div className="mt-4">
          <div
            className="text-[10px] font-bold uppercase tracking-[0.08em] opacity-60"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            02 / buscar en
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {SCOPE_OPTIONS.map((opt) => (
              <NbChip
                key={opt.value}
                active={statusFilter === opt.value}
                className="cursor-pointer"
                onClick={() => setStatusFilter(opt.value)}
              >
                {opt.label}
              </NbChip>
            ))}
          </div>
        </div>

        <NbButton
          fill="lost"
          className="mt-4 w-full py-3.5"
          disabled={!queryFile || loading}
          onClick={onSearch}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Buscando…
            </>
          ) : (
            'Buscar similares'
          )}
        </NbButton>

        {error && (
          <p
            className="mt-3 text-[12px] font-semibold"
            style={{ color: '#b42020', fontFamily: '"JetBrains Mono", monospace' }}
          >
            {error}
          </p>
        )}
      </NbCard>

      <div
        className="flex items-start gap-2.5 px-3 py-2.5"
        style={{ border: '2px solid var(--nb-ink)', background: 'var(--nb-paper)' }}
      >
        <div className="font-display text-[24px] leading-none">!</div>
        <div className="text-[12px] font-medium">
          <strong>Tip:</strong> foto de cuerpo entero con buena luz da los mejores resultados.
          Siempre revisá cada match.
        </div>
      </div>

      {matches && matches.length === 0 && (
        <p className="text-center text-gray-600">No hay coincidencias para esta foto.</p>
      )}

      {matches && matches.length > 0 && (
        <section aria-label="Resultados" className="flex w-full flex-col gap-4">
          <div
            className="text-[10px] font-bold uppercase tracking-[0.08em] opacity-60"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            / resultados
          </div>
          <ul className="grid list-none grid-cols-1 gap-6 sm:grid-cols-2">
            {matches.map((m) => (
              <li key={`${m.pet.id}-${m.matched_gallery_id}`}>
                <SimilarMatchCard
                  pet={m.pet}
                  cosineDistance={m.cosine_distance}
                  matchedImageUrl={m.matched_image_url}
                />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};
